import React from "react";
import styled, { createGlobalStyle } from "styled-components";

import Viewer from "../../UI/Viewer";
import ChapterBar from "../ChapterBar/ChapterBar";
import SwipableChapterLayout from "./SwipableChapterLayout";
import StandardChapterLayout from "./StandardChapterLayout";
import { isMobileDevice } from "../../../utils/mobile";
import { withAudio, AudioCtx } from "../ChapterAudio/ChapterAudio";
import { withChapter, ChapterCtx } from "../ChapterContainer";
import { ChapterFooter } from "./ChapterFooter";
import { useChapterMode, CHAPTER_MODES } from "./ChapterMode";
import { ChapterScroller } from "./ChapterScroller";
import { FooterContainer } from "../../Footer/Footer";
import AsyncComponent from "../../AsyncComponent";
import { useFeedbackStatus } from "../../Feedback/utils";
import media from "../../../utils/media";
import { useScriptureRouting } from "components/Bible/useScriptureRouting";
import { useParallelManager, getVerseIds } from "../utils";

const AsyncChapterFeedback = AsyncComponent(() =>
  import("../../Feedback/ChapterFeedback")
);

// NOTE: this isn't an ideal solution, but since the site footer is not nested within
// the chapter layout, and the audio provider is not within the SiteWrapper, this is the
// only means we have to communicate to the SiteFooter that it should have more padding at the bottom
// while the audio play is present.
// TODO: provide a more permanant solution when the new audio player is implemented
const SiteFooterAudioPadding = createGlobalStyle`
  ${FooterContainer} {
    margin-bottom: ${props => (props.hasAudio ? "120px" : "0")};
  }
`;

const ChapterViewer = styled(Viewer)<{
  numOfParallels: number;
  feedbackEnabled: boolean;
}>`
  padding-top: 0;

  ${({ numOfParallels, feedbackEnabled }) =>
    // only override widths if there are no parallels and feedback is enabled
    numOfParallels === 0 &&
    feedbackEnabled &&
    media.desktop`max-width: calc(14rem + 65ch + 324px);`}
`;

interface ChapterLayoutProps {
  chapter: ChapterCtx;
  audio: AudioCtx;
}

const ChapterLayout: React.FC<ChapterLayoutProps> = ({ chapter, audio }) => {
  useParallelManager();

  const { bibleId, parallelBibleIds } = useScriptureRouting();

  const currentDblId = bibleId.slice(0, -3);

  const isMobile = isMobileDevice();
  const platformType = isMobile ? "phone" : "desktop";
  const numOfParallels = parallelBibleIds.length;

  const {
    enabled: feedbackEnabled,
    visible: feedbackVisible
  } = useFeedbackStatus({ dblId: currentDblId });

  const Layout = isMobile ? SwipableChapterLayout : StandardChapterLayout;
  const layoutPlatform = isMobile ? platformType : "desktop";

  const {
    mode,
    setModeAudioSync,
    setModeDefault,
    decrementScrollLocks,
    incrementScrollLocks
  } = useChapterMode(numOfParallels > 0);

  return (
    <ChapterViewer
      feedbackEnabled={feedbackEnabled && feedbackVisible}
      numOfParallels={numOfParallels}
      wide={parallelBibleIds.length > 0}
    >
      <div
        style={{
          opacity:
            isMobile ||
            mode === CHAPTER_MODES.DEFAULT ||
            mode === CHAPTER_MODES.AUDIO_USER_SCROLLING
              ? 1
              : 0
        }}
      >
        <ChapterBar setModeDefault={setModeDefault} />
      </div>
      <Layout platformType={layoutPlatform} setModeDefault={setModeDefault}>
        {feedbackEnabled && (
          <AsyncChapterFeedback
            dblId={currentDblId}
            chapterVerseIds={getVerseIds(chapter.getChapter()?.content)}
          />
        )}
      </Layout>
      <ChapterFooter
        feedbackEnabled={feedbackEnabled}
        audioIsPlayable={audio.isPlayable}
        mode={mode}
        setModeDefault={setModeDefault}
        setModeAudioSync={setModeAudioSync}
      />
      <ChapterScroller
        mode={mode}
        currentAudioVerseElement={audio.currentAudioVerseElement}
        currentAudioVerseId={audio.currentAudioVerseId}
        incrementScrollLocks={incrementScrollLocks}
        decrementScrollLocks={decrementScrollLocks}
      />
      <SiteFooterAudioPadding hasAudio={audio.isPlayable} />
    </ChapterViewer>
  );
};

export default withChapter(withAudio(ChapterLayout));
